import { useState } from 'react';
import {
  Card, CardContent, Stack, TextField, Button, Typography, Table, TableHead, TableRow, TableCell, TableBody,
  Chip, Dialog, DialogTitle, DialogContent, DialogActions, Alert, Snackbar, InputAdornment, Box,
} from '@mui/material';
import { LocalShipping, Search } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { dealerOrdersApi, type DealerOrder } from '../../api/dealerOrders.api';
import { extractApiError } from '../../api/error';
import { useAuth } from '../../hooks/useAuth';
import { DealerSummaryCard } from '../../components/DealerSummaryCard';

const inr = (n = 0) => `₹${Number(n).toLocaleString('en-IN')}`;

/**
 * FULFILMENT → Dispatch. Orders that are allocated (stock reserved) are pending
 * dispatch. Open one, enter dispatch details and mark it Dispatched.
 */
export function DispatchPage() {
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const [search, setSearch] = useState('');
  const [target, setTarget] = useState<DealerOrder | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [form, setForm] = useState({ dispatchDate: '', transporter: '', vehicleNo: '', docketNo: '' });

  const list = useQuery({
    queryKey: ['dispatch', 'pending', search],
    // Ready to dispatch = approved (direct PDI flow) or manufactured/allocated (made-to-order flow).
    queryFn: () => dealerOrdersApi.list({ status: 'approved,manufactured,allocated', search: search || undefined, limit: 100 }),
  });

  const dispatchMut = useMutation({
    mutationFn: () => dealerOrdersApi.dispatch(target!.id, { ...form, dispatchDate: form.dispatchDate || undefined, transporter: form.transporter || undefined, vehicleNo: form.vehicleNo || undefined, docketNo: form.docketNo || undefined }),
    onSuccess: () => { setTarget(null); setForm({ dispatchDate: '', transporter: '', vehicleNo: '', docketNo: '' }); setError(null); setToast('Order dispatched'); qc.invalidateQueries({ queryKey: ['dispatch'] }); },
    onError: (e) => setError(extractApiError(e)),
  });

  return (
    <>
      <PageHeader title="Dispatch" subtitle="Allocated orders pending dispatch. Enter dispatch details and mark as dispatched." />

      <Card variant="outlined" sx={{ mb: 2 }}><CardContent sx={{ py: 1.5 }}>
        <TextField size="small" placeholder="Search by Order Request ID…" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ maxWidth: 360 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }} />
      </CardContent></Card>

      <Card variant="outlined"><CardContent>
        <Table size="small">
          <TableHead><TableRow><TableCell>Order</TableCell><TableCell>Order Date</TableCell><TableCell>Expected</TableCell><TableCell align="right">Grand Total</TableCell><TableCell>Status</TableCell><TableCell /></TableRow></TableHead>
          <TableBody>
            {(list.data?.data ?? []).map((o) => (
              <TableRow key={o.id} hover>
                <TableCell>{o.orderCode}</TableCell>
                <TableCell>{o.orderDate ?? '—'}</TableCell>
                <TableCell>{o.expectedDate ?? '—'}</TableCell>
                <TableCell align="right">{inr(o.grandTotal)}</TableCell>
                <TableCell><Chip size="small" color="info" label={o.status} /></TableCell>
                <TableCell align="right">{hasPermission('dealer_dispatch.create') && <Button size="small" startIcon={<LocalShipping />} onClick={() => { setTarget(o); setError(null); }}>Dispatch</Button>}</TableCell>
              </TableRow>
            ))}
            {(list.data?.data.length ?? 0) === 0 && <TableRow><TableCell colSpan={6}><Typography variant="body2" color="text.secondary" py={2} textAlign="center">No orders pending dispatch.</Typography></TableCell></TableRow>}
          </TableBody>
        </Table>
      </CardContent></Card>

      <Dialog open={target != null} onClose={() => setTarget(null)} fullWidth maxWidth="sm">
        <DialogTitle>Dispatch — {target?.orderCode}</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
          <Box sx={{ mt: 1, mb: 1.5 }}><DealerSummaryCard dealerId={target?.dealerId} dense /></Box>
          <Stack spacing={1.5} mt={1}>
            <TextField label="Dispatch Date" type="date" InputLabelProps={{ shrink: true }} value={form.dispatchDate} onChange={(e) => setForm((f) => ({ ...f, dispatchDate: e.target.value }))} fullWidth />
            <TextField label="Transporter" value={form.transporter} onChange={(e) => setForm((f) => ({ ...f, transporter: e.target.value }))} fullWidth />
            <Stack direction="row" spacing={1}>
              <TextField label="Vehicle No." value={form.vehicleNo} onChange={(e) => setForm((f) => ({ ...f, vehicleNo: e.target.value }))} fullWidth />
              <TextField label="Docket No." value={form.docketNo} onChange={(e) => setForm((f) => ({ ...f, docketNo: e.target.value }))} fullWidth />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTarget(null)}>Cancel</Button>
          <Button variant="contained" startIcon={<LocalShipping />} disabled={dispatchMut.isPending} onClick={() => dispatchMut.mutate()}>Mark Dispatched</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={Boolean(toast)} autoHideDuration={4000} onClose={() => setToast(null)}><Alert severity="success" onClose={() => setToast(null)}>{toast}</Alert></Snackbar>
    </>
  );
}
